import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  Users,
  Trash2,
  Key,
  Mail,
  Shield,
  X,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const ADMIN_USERS_TESTIDS = {
  addBtn: "admin-users-add-btn",
  createEmail: "admin-users-create-email",
  createName: "admin-users-create-name",
  createPassword: "admin-users-create-password",
  createRole: "admin-users-create-role",
  createSubmit: "admin-users-create-submit",
  createCancel: "admin-users-create-cancel",
  userRow: "admin-users-row",
  deleteBtn: "admin-users-delete-btn",
  changePwdBtn: "admin-users-change-pwd-btn",
  pwdCurrent: "admin-users-pwd-current",
  pwdNew: "admin-users-pwd-new",
  pwdSubmit: "admin-users-pwd-submit",
  pwdCancel: "admin-users-pwd-cancel",
};

const T = ADMIN_USERS_TESTIDS;

const ROLE_STYLES = {
  super_admin: "bg-red-50 text-red-700 border-red-200",
  admin: "bg-slate-100 text-slate-700 border-slate-200",
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
};

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "admin",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/admin/users", form);
      toast.success(`Admin ${res.data.name} berhasil dibuat`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-[#DC2626]" />
            </div>
            <div className="font-display font-bold text-base">Tambah Admin Baru</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Nama Lengkap</label>
            <input
              data-testid={T.createName}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Budi Santoso"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Email (Username)</label>
            <input
              data-testid={T.createEmail}
              type="email"
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="sales@falcom.co.id"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Password</label>
            <input
              data-testid={T.createPassword}
              type="password"
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Minimal 6 karakter"
              minLength={6}
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Role</label>
            <select
              data-testid={T.createRole}
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="admin">Admin — hanya bisa lihat leads & percakapan</option>
              <option value="super_admin">Super Admin — bisa kelola admin lain</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              data-testid={T.createSubmit}
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Buat Admin
            </button>
            <button
              data-testid={T.createCancel}
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Batal
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function ChangePasswordModal({ user, currentUser, onClose, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);
  const isSelf = user.id === currentUser.id;
  const isSuperReset = !isSelf && currentUser.role === "super_admin";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    setLoading(true);
    try {
      const body = { new_password: next };
      if (isSelf) body.current_password = current;
      await api.patch(`/admin/users/${user.id}/password`, body);
      toast.success("Password berhasil diubah");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal ubah password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <Key className="w-4 h-4 text-[#DC2626]" />
            </div>
            <div>
              <div className="font-display font-bold text-base">Ubah Password</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {isSelf ? "Password akun sendiri" : `Reset password: ${user.name}`}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {isSelf && (
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">Password Lama</label>
              <input
                data-testid={T.pwdCurrent}
                type="password"
                className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-700 mb-1.5 block">Password Baru</label>
            <input
              data-testid={T.pwdNew}
              type="password"
              className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="Minimal 6 karakter"
              minLength={6}
              required
            />
          </div>
          {isSuperReset && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">
              Anda mereset password admin lain sebagai Super Admin. User perlu login ulang dengan password baru.
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              data-testid={T.pwdSubmit}
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Simpan
            </button>
            <button
              data-testid={T.pwdCancel}
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Batal
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function AdminUsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);

  const isSuperAdmin = currentUser?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error("Gagal memuat daftar admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(`Hapus admin "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Admin ${u.name} dihapus`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal hapus admin");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <div className="font-medium text-sm text-slate-900">Kelola Admin</div>
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
          ) : (
            <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
              {users.length}
            </span>
          )}
        </div>
        {isSuperAdmin && (
          <button
            data-testid={T.addBtn}
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Tambah Admin
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" />
          Hanya Super Admin yang bisa menambah atau menghapus admin. Anda bisa ubah password akun sendiri.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/50">
                <th className="px-4 py-3 font-medium">Nama</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Dibuat</th>
                <th className="px-4 py-3 font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === currentUser?.id;
                const canChangePwd = isMe || isSuperAdmin;
                const canDelete = isSuperAdmin && !isMe;
                return (
                  <tr
                    key={u.id}
                    data-testid={T.userRow}
                    className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                        {u.name}
                        {isMe && (
                          <span className="text-[10px] bg-red-50 text-[#DC2626] border border-red-200 px-1.5 py-0.5 rounded font-medium">
                            AKUN ANDA
                          </span>
                        )}
                      </div>
                      {u.created_by && (
                        <div className="text-xs text-slate-500 mt-0.5">oleh: {u.created_by}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-slate-400" /> {u.email}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`text-xs font-medium border rounded-full px-2.5 py-1 ${ROLE_STYLES[u.role] || ROLE_STYLES.admin}`}
                      >
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-500 whitespace-nowrap">
                      {u.created_at
                        ? new Date(u.created_at).toLocaleString("id-ID", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {canChangePwd && (
                          <button
                            data-testid={T.changePwdBtn}
                            onClick={() => setPwdTarget(u)}
                            className="text-xs text-slate-600 hover:text-[#DC2626] transition-colors flex items-center gap-1"
                          >
                            <Key className="w-3 h-3" /> Password
                          </button>
                        )}
                        {canDelete && (
                          <button
                            data-testid={T.deleteBtn}
                            onClick={() => deleteUser(u)}
                            className="text-xs text-slate-500 hover:text-red-600 transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Hapus
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {showCreate && (
          <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />
        )}
        {pwdTarget && (
          <ChangePasswordModal
            user={pwdTarget}
            currentUser={currentUser}
            onClose={() => setPwdTarget(null)}
            onDone={load}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
